/**
 * YearOverYearTab
 *
 * Tab 1 orchestrator: connects the YoY controls, KPI cards, and chart
 * to the data hooks. Manages state for professional selection and granularity.
 *
 * Date ranges are fully determined by each attorney's Hourglass go-live date
 * (computed server-side) — no client-side date range selection needed.
 */

import { useState, useMemo, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { YoYGranularity } from "../../types/dashboard";
import {
  useProfessionals,
  useYoYSummary,
  useYoYTimeSeries,
  useGoLiveDates,
  useDailyView,
} from "../../hooks/useAdminData";
import YoYControls from "./YoYControls";
import YoYKpiCards from "./YoYKpiCards";
import YoYChart from "./YoYChart";
import DailyViewChart from "./DailyViewChart";
import DashboardQueryStatus from "./DashboardQueryStatus";
import { formatGoLiveDate, toLocalDateStr } from "../../utils/adminHelpers";

function computeDaysBetween(start: string, end: string): number {
  const s = new Date(start + "T00:00:00");
  const e = new Date(end + "T00:00:00");
  return Math.round((e.getTime() - s.getTime()) / (1000 * 60 * 60 * 24));
}

export default function YearOverYearTab() {
  const [profId, setProfId] = useState<string | null>(null);
  const [granularity, setGranularity] = useState<YoYGranularity>("weekly");
  const queryClient = useQueryClient();

  const {
    professionals,
    error: professionalsError,
    loading: professionalsLoading,
    refetch: retryProfessionals,
  } = useProfessionals();
  const { goLiveDates, error: goLiveError } = useGoLiveDates();

  const {
    summary,
    loading: summaryLoading,
    isFetching: summaryFetching,
    isPlaceholderData: summaryIsPlaceholder,
    error: summaryError,
  } = useYoYSummary(profId);

  const {
    data: timeSeriesData,
    loading: tsLoading,
    isFetching: tsFetching,
    isPlaceholderData: tsIsPlaceholder,
    error: tsError,
  } = useYoYTimeSeries(granularity, profId);

  const {
    data: dailyData,
    loading: dailyLoading,
    isFetching: dailyFetching,
    isPlaceholderData: dailyIsPlaceholder,
    error: dailyError,
  } = useDailyView(granularity === "daily" ? profId : null);

  // Auto-reset to weekly when "All Professionals" is selected while on daily view
  useEffect(() => {
    if (!profId && granularity === "daily") setGranularity("weekly");
  }, [profId, granularity]);

  // Build prof_id → go_live_date map for chart subtitles
  const goLiveDateMap = useMemo(() => {
    const map: Record<string, string> = {};
    for (const rec of goLiveDates) {
      map[rec.prof_id] = rec.go_live_date;
    }
    return map;
  }, [goLiveDates]);

  // Only show the empty state after both independent panels have settled.
  const hardLoading =
    summaryLoading || (granularity === "daily" ? dailyLoading : tsLoading);

  // Soft loading: dim the content while waiting for data. Includes first-time daily
  // loading (dailyLoading) so granularity switches feel consistent with professional
  // switches — opacity dim rather than spinner in both cases.
  const isShowingPlaceholder =
    summaryIsPlaceholder ||
    (granularity === "daily"
      ? dailyIsPlaceholder || dailyLoading
      : tsIsPlaceholder);

  // Transitioning: any background fetch in progress — drives "Updating…" subtitle
  const transitioning =
    (summaryFetching || tsFetching || dailyFetching) && !hardLoading;

  // First non-null error to surface in the UI (Sentry capture is handled centrally
  // in QueryCache.onError in queryClient.ts)
  const activeError =
    summaryError ?? (granularity === "daily" ? dailyError : tsError);

  const goLiveDate = profId ? (goLiveDateMap[profId] ?? null) : null;
  const today = toLocalDateStr();

  return (
    <div className="space-y-6">
      <YoYControls
        professionals={professionals}
        profId={profId}
        onProfIdChange={setProfId}
        granularity={granularity}
        onGranularityChange={setGranularity}
      />
      <DashboardQueryStatus
        label="professionals"
        loading={professionalsLoading}
        error={professionalsError}
        onRetry={() => void retryProfessionals()}
      />
      <DashboardQueryStatus
        label="activity dates"
        error={goLiveError}
        onRetry={() =>
          void queryClient.invalidateQueries({
            queryKey: ["go-live-dates"],
            exact: true,
          })
        }
      />
      <DashboardQueryStatus
        label="billing summary"
        loading={summaryLoading}
        updating={summaryFetching && !summaryLoading}
        error={summaryError}
        onRetry={() =>
          void queryClient.invalidateQueries({
            queryKey: ["yoy-summary", profId ?? "all"],
            exact: true,
          })
        }
      />
      <DashboardQueryStatus
        label="billing chart"
        loading={granularity === "daily" ? dailyLoading : tsLoading}
        updating={
          granularity === "daily"
            ? dailyFetching && !dailyLoading
            : tsFetching && !tsLoading
        }
        error={granularity === "daily" ? dailyError : tsError}
        onRetry={() =>
          void queryClient.invalidateQueries({
            queryKey:
              granularity === "daily"
                ? ["daily-view", profId ?? "none"]
                : ["yoy-timeseries", granularity, profId ?? "all"],
            exact: true,
          })
        }
      />
      <div className={`space-y-6${isShowingPlaceholder ? " opacity-60" : ""}`}>
        {/* KPI Cards */}
        {summary && <YoYKpiCards summary={summary} granularity={granularity} />}

        {/* Daily line chart — single attorney only */}
        {granularity === "daily" && profId && dailyData && (
          <div className="bg-card border border-border rounded-lg p-6">
            <h3 className="text-sm font-semibold text-foreground">
              Daily Hours Moving Average
            </h3>
            {transitioning ? (
              <p className="text-xs text-muted-foreground mt-0.5 mb-4">
                Updating…
              </p>
            ) : goLiveDate ? (
              <p className="text-xs text-muted-foreground mt-0.5 mb-4">
                Go-live: {formatGoLiveDate(goLiveDate)} · Comparing{" "}
                {computeDaysBetween(goLiveDate, today)} days
              </p>
            ) : (
              <div className="mb-4" />
            )}
            <DailyViewChart data={dailyData} />
          </div>
        )}

        {/* Weekly/monthly bar chart */}
        {granularity !== "daily" && timeSeriesData.length > 0 && (
          <div className="bg-card border border-border rounded-lg p-6">
            <h3 className="text-sm font-semibold text-foreground">
              Hours Comparison —{" "}
              {granularity === "weekly" ? "Weekly" : "Monthly"}
            </h3>
            {transitioning ? (
              <p className="text-xs text-muted-foreground mt-0.5 mb-4">
                Updating…
              </p>
            ) : goLiveDate ? (
              <p className="text-xs text-muted-foreground mt-0.5 mb-4">
                Go-live: {formatGoLiveDate(goLiveDate)} · Comparing{" "}
                {computeDaysBetween(goLiveDate, today)} days
              </p>
            ) : (
              <div className="mb-4" />
            )}
            <YoYChart
              data={timeSeriesData}
              granularity={granularity}
              selectedProfId={profId}
              goLiveDates={goLiveDateMap}
            />
          </div>
        )}
      </div>

      {/* Empty state — only when fully loaded with no data and no error */}
      {!hardLoading &&
        !activeError &&
        !summary &&
        timeSeriesData.length === 0 && (
          <p className="text-center text-muted-foreground text-sm py-12">
            No billing data available. Data will appear here once billing
            transactions are synced and attorneys have Hourglass activity.
          </p>
        )}
    </div>
  );
}
